/**
 * GetCreditsModal — the out-of-credits flow: buy a pack (Stripe Checkout),
 * redeem a one-time code, or open the feedback form to earn a credit.
 * Opened when an import is attempted with 0 credits (or a 402 comes back).
 */
import { useState } from 'react';

import { Modal } from './ui/modal';
import { Button } from './ui/button';
import BuyCreditsPanel from './BuyCreditsPanel';
import RedeemCodeForm from './RedeemCodeForm';
import FeedbackDialog from './FeedbackDialog';

/** Props for {@link GetCreditsModal}. */
export interface GetCreditsModalProps {
  onClose: () => void;
  /** Called with the new balance after a successful code redeem. */
  onRedeemed?: (_balance: number) => void;
  /** Headline override (default: out-of-credits copy). */
  title?: string;
}

/** Out-of-credits modal: buy, redeem, or earn via feedback. */
export default function GetCreditsModal({
  onClose,
  onRedeemed,
  title,
}: GetCreditsModalProps) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  if (feedbackOpen) {
    return <FeedbackDialog onClose={() => setFeedbackOpen(false)} />;
  }

  return (
    <Modal
      label="Get import credits"
      title={title ?? 'You’re out of import credits'}
      subtitle="Each credit converts one estimate PDF to a Xactimate-ready ESX. Unused credits never expire."
      onClose={onClose}
      testId="get-credits-modal"
    >
      <div className="space-y-4">
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[.05em] text-[var(--tx-lo)]">
            Buy credits
          </div>
          <BuyCreditsPanel />
        </div>
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[.05em] text-[var(--tx-lo)]">
            Have a code?
          </div>
          <RedeemCodeForm
            onRedeemed={(balance) => {
              onRedeemed?.(balance);
            }}
          />
        </div>
        <div className="border-t border-[var(--bd-subtle)] pt-3">
          <Button variant="ghost" size="sm" onClick={() => setFeedbackOpen(true)}>
            Earn a free credit — give feedback
          </Button>
        </div>
      </div>
    </Modal>
  );
}
